import PdfTronViewer, { Core, WebViewerInstance } from '@pdftron/webviewer';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { noop, storeFactory } from '@frontend/utils';

import { PDF_TRON_KEY, PDF_TRON_PATH } from 'constants/all';

import {
  getAnnotationViewportRect,
  type AnnotationViewportRect,
  getViewerScrollRect,
} from './annotationCommands/suggestionUtils';

// WebViewer custom styles as a string to inject directly
const webViewerStyles = `
:host .DocumentContainer,
:host .DocumentContainer * {
  font-family: Impact, cursive !important;
}

:host .ModularHeader:has(:focus-visible) {
  outline: none !important;
}

:host .units-picker-custom-element > div {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px;
}
  
:host .units-picker-custom-element > div > select {
  padding: 4px;
  border-color: #bdbdbd;
}
  
:host .units-picker-custom-element > div > select:focus {
  border-color: #333;
}

:host .extractions-toggle-custom-element {
  display: flex;
  align-items: center;
}

:host .extractions-toggle-custom-element > div {
  display: flex;
  align-items: center;
  padding: 4px;
}

`;

interface PDFStoreState extends Record<string, unknown> {
  filename: string | null;
  originalUrl: string | null;
  newFile: File | null;
  isOriginalFileVisible: boolean;
  isNewFileVisible: boolean;
  isSnappingEnabled: boolean;
}

// NOTE: we are using a separate store outside of the react lifestyle because we need
//  a mechanism to pass state into the render methods of various toolbars and panels
//  We use this to pass information to custom panels and custom snapping checkbox
//  docs: https://docs.apryse.com/api/web/UI.html#.setCustomPanel
//  Right now, the strategy to pass in external state tied to react or redux is via
//  the pdfStore. This prevents redundant re-renders or performing jiu-jitsu with manipulating
//  custom additions to the pdftron UI (examples are custom compare panel and SnappingCheckbox)
//  There is probably a way to get this work within the react lifecyle, by updating
//  the panel any time external state changes, but we will leave that for a separate refactor
export const pdfStore = storeFactory<PDFStoreState>(
  [
    'filename',
    'originalUrl',
    'newFile',
    'isOriginalFileVisible',
    'isNewFileVisible',
    'isSnappingEnabled',
  ],
  {},
  {
    filename: null,
    originalUrl: null,
    newFile: null,
    isOriginalFileVisible: true,
    isNewFileVisible: true,
    isSnappingEnabled: true,
  },
);

const useWebViewerContextValue = () => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const [instance, setInstance] = useState<WebViewerInstance | null>(null);
  const webComponentRef = useRef<Element | null>(null);

  const initializeWebViewer = useCallback((viewerElement: HTMLElement) => {
    PdfTronViewer.WebComponent(
      {
        useDownloader: false,
        licenseKey: PDF_TRON_KEY,
        path: PDF_TRON_PATH,
        fullAPI: true,
        enableMeasurement: true,
        enableRedaction: true,
      },
      viewerElement,
    )
      .then((instance) => {
        // Manually inject the custom styles into the WebViewer's Shadow DOM
        try {
          const webViewerElement =
            viewerElement.querySelector('apryse-webviewer');
          if (webViewerElement && webViewerElement.shadowRoot) {
            const styleElement = document.createElement('style');
            styleElement.textContent = webViewerStyles;
            webViewerElement.shadowRoot.appendChild(styleElement);
            console.warn('Custom styles injected successfully');
          } else {
            console.warn(
              'WebViewer element or shadowRoot not found, trying alternative method',
            );
            // Alternative: inject into the main document
            const styleElement = document.createElement('style');
            styleElement.textContent = webViewerStyles;
            document.head.appendChild(styleElement);
            console.warn('Custom styles injected into document head');
          }
          webComponentRef.current = webViewerElement;
        } catch (error) {
          console.error('Error injecting custom styles:', error);
        }

        setInstance(instance);
      })
      .catch((error) => {
        console.error('Error initializing WebViewer:', error);
      });
  }, []);

  /**
   * Ensures the WebViewer web component is present inside the given container.
   *
   * - First mount: creates the WebViewer instance via `initializeWebViewer`.
   * - Subsequent mounts (e.g. after the container remounts due to loading
   *   states in ViewerContent): re-attaches the existing `<apryse-webviewer>`
   *   element to the new container without re-initializing.
   *
   * This is needed because the context provider lives above the component that
   * owns the container div, so the instance outlives the container's DOM lifecycle.
   */
  const attachWebViewer = useCallback(
    (container: HTMLElement) => {
      if (!instance) {
        initializeWebViewer(container);
        return;
      }

      const webComponent = webComponentRef.current;
      if (container.childElementCount === 0 && webComponent) {
        container.appendChild(webComponent);
      }
    },
    [initializeWebViewer, instance],
  );

  // reset the pdfStore values for pdf compare when the pdfviewer mounts (this prevents it from carrying over old state, since it is non-react)
  // NOTE: this must be its own effect with no dependencies - otherwise it will throw off updates to load cycle
  useEffect(() => {
    pdfStore.filename = null;
    pdfStore.originalUrl = null;
    pdfStore.newFile = null;
    pdfStore.isOriginalFileVisible = true;
    pdfStore.isNewFileVisible = true;
    pdfStore.isSnappingEnabled = true;
  }, []);

  // Suppress known benign PDFTron internal promise rejections that occur when a
  // document is closed while async operations (e.g. setPageLabels) are still
  // in-flight. This happens during rapid document switching and is harmless —
  // PDFTron recovers correctly — but without a handler these surface as
  // unhandled rejections that pollute the console and error trackers.
  useEffect(() => {
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message: string =
        typeof reason === 'string' ? reason : reason?.message ?? String(reason);
      const isPdftronCancellation =
        message.includes('setPageLabels is cancelled') ||
        (reason?.type === 'JavascriptError' &&
          message.includes('function signature mismatch'));
      if (isPdftronCancellation) {
        event.preventDefault();
      }
    };
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, []);

  // handle unmounting
  useEffect(() => {
    return () => {
      if (instance) {
        instance.Core.documentViewer.dispose();
        instance.UI.dispose();
      }
    };
  }, [instance]);

  /**
   * Returns the PDFTron annotation with the given Id, or null if not found.
   * Used by the hoisted flyout hook to look up the focused annotation.
   */
  const getAnnotationById = useCallback(
    (annotationId: string): Core.Annotations.Annotation | null => {
      if (!instance) return null;
      const annotations = instance.Core.annotationManager.getAnnotationsList();
      return annotations.find((a) => a.Id === annotationId) ?? null;
    },
    [instance],
  );

  /**
   * Returns a Popper VirtualElement positioned over the given annotation in
   * the current viewport, or null when the annotation or document is not ready.
   */
  const getAnnotationRect = useCallback(
    (annotationId: string): AnnotationViewportRect | null => {
      if (!instance) return null;
      const annotation = getAnnotationById(annotationId);
      if (!annotation) return null;
      return getAnnotationViewportRect(annotation, instance);
    },
    [instance, getAnnotationById],
  );

  /**
   * Returns the bounding rect of the usable PDF canvas area in viewport
   * coordinates, accounting for the internal left panel width. Returns null
   * when the viewer is not yet initialised.
   */
  const getScrollViewRect = useCallback((): DOMRect | null => {
    if (!instance || !viewerRef.current) return null;
    return getViewerScrollRect(viewerRef.current);
  }, [instance, viewerRef]);

  return {
    instance,
    setInstance,
    isLoaded: !!instance,
    attachWebViewer,
    getAnnotationById,
    getAnnotationRect,
    getScrollViewRect,
    viewerRef,
  };
};

export type WebViewerContextType = ReturnType<typeof useWebViewerContextValue>;

export const WebViewerContext = createContext<WebViewerContextType>({
  instance: null,
  setInstance: noop,
  isLoaded: false,
  attachWebViewer: noop,
  getAnnotationById: () => null,
  getAnnotationRect: () => null,
  getScrollViewRect: () => null,
  viewerRef: { current: null },
});

export const WebViewerContextProvider = ({
  children,
}: React.PropsWithChildren<unknown>) => {
  const webViewerState = useWebViewerContextValue();

  return (
    <WebViewerContext.Provider value={webViewerState}>
      {children}
    </WebViewerContext.Provider>
  );
};

export const useWebViewerContext = () => {
  return useContext<WebViewerContextType>(WebViewerContext);
};
